/**
 * Money handling for Harbor.
 *
 * Every monetary value in this codebase is an integer count of the currency's
 * minor unit (cents for USD/EUR, paise for INR, ...). Floating point is never
 * used for storage or arithmetic — only for display, at the very last step.
 *
 * The reason is mundane but important: `0.1 + 0.2 === 0.30000000000000004`.
 * A cart that totals 0.30000000000000004 is a cart that fails a payment
 * processor's amount check, and a tax line that rounds differently on the
 * client than on the server is a support ticket. Integers remove the entire
 * class of bug.
 */

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'INR' | 'AED';

/** Number of minor units in one major unit, per currency. */
const MINOR_UNITS: Record<CurrencyCode, number> = {
  USD: 100,
  EUR: 100,
  GBP: 100,
  INR: 100,
  AED: 100,
};

export function minorUnits(currency: CurrencyCode): number {
  return MINOR_UNITS[currency] ?? 100;
}

/** 12.5% expressed in basis points. Basis points keep tax rates integral. */
export type BasisPoints = number;

export function applyRate(amountMinor: number, rate: BasisPoints): number {
  return Math.round((amountMinor * rate) / 10_000);
}

export function formatMoney(
  amountMinor: number,
  currency: CurrencyCode = 'USD',
  locale = 'en-US',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / minorUnits(currency));
}

/**
 * Parse a user-entered price ("19.99", "19,99", "$19.99") into minor units.
 * Returns null when the input is not a usable positive amount, so callers can
 * turn that into a 422 rather than silently writing a zero price.
 */
export function parseMoney(input: string, currency: CurrencyCode = 'USD'): number | null {
  const cleaned = input.replace(/[^\d.,-]/g, '').trim();
  if (!cleaned) return null;

  // Treat the last separator as the decimal separator only when it is followed
  // by exactly two digits; otherwise it is a thousands separator.
  const normalised = /[.,]\d{2}$/.test(cleaned)
    ? cleaned.replace(/[.,](?=\d{2}$)/, '.')
    : cleaned;

  const value = Number(normalised.replace(/,/g, ''));
  if (!Number.isFinite(value) || value < 0) return null;

  return Math.round(value * minorUnits(currency));
}

export interface LineItem {
  unitPriceMinor: number;
  quantity: number;
}

export interface Totals {
  subtotalMinor: number;
  shippingMinor: number;
  taxMinor: number;
  totalMinor: number;
}

export interface TotalsOptions {
  /** Flat shipping rate, waived entirely once the free-shipping threshold is met. */
  shippingFlatMinor?: number;
  freeShippingThresholdMinor?: number;
  /** Tax rate in basis points, applied to the subtotal only. */
  taxRateBps?: BasisPoints;
}

/**
 * The single source of truth for order arithmetic. Both the API (when it
 * creates the order) and the storefront (when it previews the total) call this
 * exact function, which is why `@harbor/shared` exists as a package instead of
 * duplicated helpers. A mismatch between the number the customer saw and the
 * number the server charged is the one bug you cannot ship.
 */
export function computeTotals(lines: LineItem[], options: TotalsOptions = {}): Totals {
  const {
    shippingFlatMinor = 0,
    freeShippingThresholdMinor = Number.POSITIVE_INFINITY,
    taxRateBps = 0,
  } = options;

  const subtotalMinor = lines.reduce(
    (sum, line) => sum + line.unitPriceMinor * line.quantity,
    0,
  );

  const shippingMinor =
    subtotalMinor === 0 || subtotalMinor >= freeShippingThresholdMinor ? 0 : shippingFlatMinor;

  const taxMinor = applyRate(subtotalMinor, taxRateBps);

  return {
    subtotalMinor,
    shippingMinor,
    taxMinor,
    totalMinor: subtotalMinor + shippingMinor + taxMinor,
  };
}
